import { ChevronDown } from 'lucide-react';

/**
 * Ask Hauser, the one shape everywhere it appears (The Home's read, The Area, HOA):
 * - a header: the brand mark and "Ask Hauser", with an optional row of underline topic tabs beneath it;
 * - the questions as a quiet list: each in full (wraps, never truncated), hairlines between, muted until hovered, when it
 *   brightens on a tonal tile and its chevron nudges;
 * - a "Full … report ›" link to close.
 * Styles live in this file's own class names so no other list can collide with them.
 */
export interface AskHauserQuestion {
  label: string;
  onAsk: () => void;
}

export interface AskHauserTopics<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (topic: T) => void;
  format?: (topic: T) => string;
  ariaLabel?: string;
}

const MARK = (
  <svg width="16" height="16" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M50 100C77.6142 100 100 77.6142 100 50C100 22.3858 77.6142 0 50 0C22.3858 0 0 22.3858 0 50C0 77.6142 22.3858 100 50 100Z"
      fill="#C0FF02"
    />
    <path
      d="M56.2133 22.2267H43.7733L31.4266 77.7867H68.56L56.2133 22.2267ZM44.2266 67.7067L49.7066 40.3067L49.96 38.32L50.2133 40.3067L55.76 67.7067H44.24H44.2266Z"
      fill="#262626"
    />
  </svg>
);

const CSS =
  // a hairline between questions, inset to the text so it lines up with the header; it gives way under a hovered row
  '.hx-askl-q + .hx-askl-q::before { content: ""; position: absolute; top: 0; left: 10px; right: 10px; height: 1px; ' +
  'background: var(--tx-line); } ' +
  '.hx-askl-q:hover::before, .hx-askl-q:hover + .hx-askl-q::before { opacity: 0; } ' +
  '.hx-askl-q { color: var(--tx-muted); transition: background 0.12s ease, color 0.12s ease; } ' +
  '.hx-askl-q:hover, .hx-askl-q:focus-visible { background: var(--tx-fill); color: var(--tx-text); outline: none; } ' +
  '.hx-askl-q > svg { color: var(--tx-faint); transition: color 0.12s ease, transform 0.12s ease; } ' +
  '.hx-askl-q:hover > svg { color: var(--tx-text); transform: rotate(-90deg) translateX(-2px) !important; } ' +
  '.hx-askl-tab { color: var(--tx-muted); transition: color 0.12s ease; } .hx-askl-tab:hover { color: var(--tx-text); } ' +
  '.hx-askl-report { color: var(--tx-muted); transition: color 0.12s ease; } .hx-askl-report:hover { color: var(--tx-text); }';

export function AskHauserList<T extends string>({
  questions = [],
  topics,
  report,
  spread = false,
  comfortable = false,
  style,
}: {
  questions?: AskHauserQuestion[];
  topics?: AskHauserTopics<T>;
  report?: { label: string; onOpen: () => void };
  /** The roomy 27" version: a larger header with the tabs on its row, right-aligned, and roomier, slightly larger rows. */
  spread?: boolean;
  /** Laptop beside a radar: the tabs and questions sit lower under the eyebrow, with a little more room per row. */
  comfortable?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
      <style>{CSS}</style>
      <div
        style={{
          // the eyebrow, then the topic tabs on their own row beneath it; roomy: one row, tabs right-aligned
          display: 'flex',
          flexDirection: spread ? 'row' : 'column',
          flexWrap: 'wrap',
          alignItems: spread ? 'center' : 'flex-start',
          justifyContent: spread ? 'space-between' : undefined,
          gap: spread ? '10px 24px' : comfortable ? '30px' : 10,
          marginBottom: questions.length ? (spread ? 14 : 8) : 0,
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            // the lockup at card-title scale wherever there's room (27", or beside the radar on a laptop)
            gap: spread || comfortable ? 10 : 8,
            flexShrink: 0,
            marginTop: comfortable ? 6 : undefined,
            color: spread || comfortable ? 'var(--tx-text)' : 'var(--tx-muted)',
            fontSize: spread || comfortable ? 17 : 12,
            fontWeight: spread || comfortable ? 400 : 600,
            letterSpacing: spread || comfortable ? '-0.015em' : undefined,
          }}>
          <span style={{ display: 'inline-flex', transform: spread || comfortable ? 'scale(1.25)' : undefined }}>{MARK}</span>
          <span>Ask Hauser</span>
        </div>
        {topics && (
          <div
            role="tablist"
            aria-label={topics.ariaLabel ?? 'Topics'}
            style={{
              // text tabs, no track or thumb
              display: 'flex',
              gap: 16,
              maxWidth: '100%',
              overflowX: 'auto',
              scrollbarWidth: 'none',
            }}>
            {topics.options.map((topic) => {
              const active = topics.value === topic;
              return (
                <button
                  key={topic}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => topics.onChange(topic)}
                  className="hx-askl-tab"
                  style={{
                    // underline tabs (HOA's original): the current topic in ink at 600 over a 2px lime rule
                    height: 28,
                    padding: 0,
                    border: 0,
                    borderBottom: `2px solid ${active ? 'var(--tx-lime)' : 'transparent'}`,
                    background: 'transparent',
                    color: active ? 'var(--tx-text)' : undefined,
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 12,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {topics.format ? topics.format(topic) : topic}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {questions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '0 -10px',
          }}>
          {questions.map((q) => (
            <button
              key={q.label}
              type="button"
              className="hx-askl-q"
              onClick={q.onAsk}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                position: 'relative',
                padding: spread ? '13px 10px' : comfortable ? '12px 10px' : '10px 10px',
                border: 0,
                borderRadius: 10,
                background: 'transparent',
                width: '100%',
                textAlign: 'left',
                fontFamily: "'Manrope', sans-serif",
                fontSize: spread ? 14 : 13,
                fontWeight: 500,
                lineHeight: 1.45,
                cursor: 'pointer',
              }}>
              <span style={{ flex: 1, minWidth: 0, textWrap: 'pretty' }}>{q.label}</span>
              <ChevronDown size={14} style={{ transform: 'rotate(-90deg)', flexShrink: 0, marginTop: 2 }} />
            </button>
          ))}
        </div>
      )}
      {report && (
        <button
          type="button"
          className="hx-askl-report"
          onClick={report.onOpen}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 6,
            marginTop: questions.length ? (spread ? 18 : 14) : 10,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 12,
            fontWeight: 600,
          }}>
          {report.label}
          <ChevronDown size={12} style={{ transform: 'rotate(-90deg)' }} />
        </button>
      )}
    </div>
  );
}
